import gsap from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { createApp, onMounted, onUnmounted, ref, watch } from 'vue';

import { useViewportSize } from '~/composables/use-viewport-size';
import {
  ANIMATION_CONFIGS,
  ANIMATION_DURATIONS,
  ANIMATION_EASES,
  DESKTOP_CIRCLE_POSITIONS,
} from '~/constants/animations';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

gsap.registerPlugin(MotionPathPlugin);

const SECTION_NAME = 'brand-message';

/**
 * Brand Message Section
 *
 * This section includes a heading, a short paragraph, and a CTA with animated circles and pills.
 * The section implements an inView animation using GSAP when the section comes into view.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class BrandMessage extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const sectionInstance = this;
    const isHomePageTemplateBol = this.isHomePageTemplate();

    createApp({
      name: 'BrandMessageSection',
      setup() {
        const circleTimeline = ref(null);
        const marqueeAnimation = ref(null);

        // We need to store the timelines in the instance data so we can access them in the inView animation timeline
        instanceData.circleTimeline = circleTimeline;
        instanceData.marqueeAnimation = marqueeAnimation;

        const { isMobile } = useViewportSize();

        /**
         * Handles circle animations for mobile viewport
         * @param {NodeList} circles - Circle elements to animate
         * @param {gsap.timeline} tl - GSAP timeline to add animations to
         */
        const handleMobileCircleAnimation = (circles, tl) => {
          circles.forEach((circle, index) => {
            const startPosition = index / 3; // Evenly space the circles

            tl.to(
              circle,
              {
                duration: ANIMATION_DURATIONS.ELEMENT.CIRCLE_ANIMATION.MOBILE,
                ease: 'none',
                motionPath: {
                  align: '.motion-path-pill',
                  alignOrigin: [0.5, 0.5],
                  autoRotate: true,
                  end: startPosition + 1,
                  path: '.motion-path-pill',
                  start: startPosition,
                },
                repeat: -1,
              },
              0,
            );
          });
        };

        /**
         * Handles circle animations for desktop viewport
         * @param {NodeList} circles - Circle elements to animate
         * @param {gsap.timeline} tl - GSAP timeline to add animations to
         */
        const handleDesktopCircleAnimation = (circles = [], tl) => {
          if (!tl) return;

          circles.forEach((circle, index) => {
            const { end, start } = DESKTOP_CIRCLE_POSITIONS[index];

            tl.to(
              circle,
              {
                duration: ANIMATION_DURATIONS.ELEMENT.CIRCLE_ANIMATION.DESKTOP,
                ease: ANIMATION_EASES.POWER3_OUT,
                motionPath: {
                  align: '.motion-path-pill',
                  alignOrigin: [0.5, 0.5],
                  autoRotate: true,
                  end,
                  path: '.motion-path-pill',
                  start,
                },
              },
              0,
            );
          });

          const callouts = container.querySelectorAll('.brand-message-callouts-text');

          if (!callouts.length) return;

          tl.to(callouts, ANIMATION_CONFIGS.FADE_IN, 3);
          tl.to(callouts, ANIMATION_CONFIGS.STAGGERED_MOVE_UP, 3);
        };

        /**
         * Sets up circle animations based on viewport size
         * @param {Object} isMobile - Reactive reference to mobile viewport state
         * @param {Boolean} isMobile.value - Mobile viewport state
         */
        const setupCircleAnimations = (isMobile) => {
          const circles = container.querySelectorAll('.circle');

          if (!circles.length) return;

          // Clean up existing animation
          circleTimeline.value?.kill();

          // Create new timeline
          const tl = gsap.timeline({
            paused: true,
          });

          // Apply appropriate animation based on viewport
          if (isMobile.value) {
            handleMobileCircleAnimation(circles, tl);
          } else {
            handleDesktopCircleAnimation(circles, tl);
          }

          circleTimeline.value = tl;
        };

        /**
         * Sets up marquee animation for mobile viewport
         * @param {Object} isMobile - Reactive reference to mobile viewport state
         * @param {Boolean} isMobile.value - Mobile viewport state
         */
        const setupMarqueeAnimation = (isMobile) => {
          const marqueeContent = container.querySelectorAll('.marquee-content');

          if (!marqueeContent.length) return;

          // Clean up existing animation if switching from mobile to desktop
          if (!isMobile.value) {
            marqueeContent[0].style = '';
            marqueeAnimation.value?.kill();
            marqueeAnimation.value = null;
            return;
          }

          const contentWidth = marqueeContent[0].offsetWidth;

          marqueeAnimation.value = gsap.to(marqueeContent, {
            duration: ANIMATION_DURATIONS.ELEMENT.MARQUEE,
            ease: 'none',
            paused: true,
            repeat: -1,
            x: `-=${contentWidth}`,
          });
        };

        watch(isMobile, () => {
          sectionInstance.setupAnimation(instanceData, () =>
            sectionInstance.setupInViewAnimationTimeline(instanceData),
          );
          sectionInstance.setupInViewAnimation(instanceData);

          setupCircleAnimations(isMobile);
          setupMarqueeAnimation(isMobile);
        });

        onMounted(() => {
          setupCircleAnimations(isMobile);
          setupMarqueeAnimation(isMobile);

          // Hook analytics event to CTA links
          if (isHomePageTemplateBol) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        onUnmounted(() => {
          if (marqueeAnimation.value) {
            marqueeAnimation.value.kill();
          }
          if (circleTimeline.value) {
            circleTimeline.value.kill();
          }
        });

        return {};
      },
    }).mount(container);
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) {
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        start: 'top 90%',
        trigger: container,
      },
    });

    if (!tl) {
      return null;
    }

    const content = container.querySelector('.brand-message-content');
    const marqueeContainer = container.querySelector('.marquee-container');

    if (content) {
      tl.to(content, ANIMATION_CONFIGS.FADE_IN, 0);
      tl.to(content, ANIMATION_CONFIGS.MOVE_UP, 0);
    }

    if (marqueeContainer) {
      tl.to(marqueeContainer, ANIMATION_CONFIGS.FADE_IN, 0);
    }

    tl.add(() => {
      if (instanceData.circleTimeline && instanceData.circleTimeline.value) {
        instanceData.circleTimeline.value.play();
      }
    }, 0);

    tl.add(() => {
      if (instanceData.marqueeAnimation && instanceData.marqueeAnimation.value) {
        instanceData.marqueeAnimation.value.play();
      }
    }, 0);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new BrandMessage();
});
